import React from "react";
import { motion } from "framer-motion";
import { UserSession } from "@/stores/collaboration";

interface UserAvatarProps {
  user: UserSession;
  size?: "sm" | "md" | "lg";
  showStatus?: boolean;
  showName?: boolean;
  interactive?: boolean;
  onClick?: (user: UserSession) => void;
  className?: string;
}

const sizeClasses = {
  sm: "w-6 h-6 text-xs",
  md: "w-8 h-8 text-sm",
  lg: "w-10 h-10 text-base",
};

const statusColors = {
  online: "bg-green-500",
  idle: "bg-yellow-500",
  offline: "bg-gray-400",
};

export const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = "md",
  showStatus = true,
  showName = false,
  interactive = false,
  onClick,
  className = "",
}) => {
  const sizeClass = sizeClasses[size];
  const statusColor = statusColors[user.isOnline ? "online" : "offline"];

  const initials =
    user.user?.firstName && user.user?.lastName
      ? `${user.user.firstName[0]}${user.user.lastName[0]}`
      : user.user?.username?.substring(0, 2).toUpperCase() ||
        user.userId?.substring(0, 2).toUpperCase() || "??";

  return (
    <div
      className={`relative inline-flex items-center gap-2 ${interactive ? "cursor-pointer" : ""} ${className}`}
      onClick={() => interactive && onClick?.(user)}
    >
      <motion.div
        className={`relative ${sizeClass} rounded-full flex items-center justify-center font-medium text-white shadow-lg select-none overflow-hidden`}
        style={{ backgroundColor: user.color }}
        initial={{ scale: 0 }}
        animate={{
          scale: 1,
          boxShadow:
            user.currentAction !== "idle"
              ? `0 0 0 2px ${user.color}40`
              : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        }}
        whileHover={interactive ? { scale: 1.05 } : undefined}
        whileTap={interactive ? { scale: 0.95 } : undefined}
        transition={{
          type: "spring",
          damping: 25,
          stiffness: 300,
          duration: 0.2,
        }}
      >
        {user.user?.avatar ? (
          <img
            src={user.user.avatar}
            alt={user.user?.username || user.userId}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to initials if image fails to load
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <span>{initials}</span>
        )}

        {/* Activity pulse animation */}
        {user.currentAction !== "idle" && (
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{ backgroundColor: user.color }}
            initial={{ scale: 1, opacity: 0.5 }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        )}

        {/* Status indicator */}
        {showStatus && (
          <motion.div
            className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 ${statusColor} rounded-full border-2 border-white shadow-sm`}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1 }}
          />
        )}

        {/* Current action indicator */}
        {user.currentAction === "dragging" && (
          <motion.div
            className="absolute -top-1 -right-1 text-xs"
            initial={{ scale: 0, rotate: -15 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 15 }}
          >
            🖱️
          </motion.div>
        )}

        {user.currentAction === "editing" && (
          <motion.div
            className="absolute -top-1 -right-1 text-xs"
            initial={{ scale: 0, rotate: -15 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 15 }}
          >
            ✏️
          </motion.div>
        )}
      </motion.div>

      {/* User name */}
      {showName && (
        <motion.span
          className="text-sm text-gray-700 font-medium max-w-24 truncate"
          initial={{ opacity: 0, x: -5 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          {user.user?.username || `User ${user.userId?.slice(-4)}`}
        </motion.span>
      )}
    </div>
  );
};

// Avatar group component for displaying multiple users
export const UserAvatarGroup: React.FC<{
  users: UserSession[];
  maxVisible?: number;
  size?: "sm" | "md" | "lg";
  showStatus?: boolean;
  interactive?: boolean;
  onUserClick?: (user: UserSession) => void;
  className?: string;
}> = ({
  users,
  maxVisible = 5,
  size = "md",
  showStatus = true,
  interactive = false,
  onUserClick,
  className = "",
}) => {
  const visibleUsers = users.slice(0, maxVisible);
  const hiddenCount = Math.max(0, users.length - maxVisible);

  return (
    <div className={`flex items-center -space-x-2 ${className}`}>
      {visibleUsers.map((user, index) => (
        <motion.div
          key={user.userId}
          initial={{ opacity: 0, scale: 0, x: -10 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{ opacity: 0, scale: 0, x: 10 }}
          transition={{ delay: index * 0.05 }}
          style={{ zIndex: visibleUsers.length - index }}
        >
          <UserAvatar
            user={user}
            size={size}
            showStatus={showStatus}
            interactive={interactive}
            onClick={onUserClick}
          />
        </motion.div>
      ))}

      {hiddenCount > 0 && (
        <motion.div
          className={`relative ${sizeClasses[size]} rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium shadow-lg border-2 border-white`}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: visibleUsers.length * 0.05 }}
        >
          +{hiddenCount}
        </motion.div>
      )}
    </div>
  );
};

// Active user indicator for showing who's currently working
export const ActiveUserIndicator: React.FC<{
  user: UserSession;
  position: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  className?: string;
}> = ({ user, position = "top-right", className = "" }) => {
  const positionClasses = {
    "top-left": "top-2 left-2",
    "top-right": "top-2 right-2",
    "bottom-left": "bottom-2 left-2",
    "bottom-right": "bottom-2 right-2",
  };

  if (user.currentAction === "idle" || !user.isOnline) return null;

  return (
    <motion.div
      className={`fixed ${positionClasses[position]} z-40 ${className}`}
      initial={{ opacity: 0, scale: 0, y: position.includes("top") ? -20 : 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0, y: position.includes("top") ? -20 : 20 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
    >
      <div className="flex items-center gap-2 bg-white rounded-full px-3 py-1 shadow-lg border">
        <UserAvatar user={user} size="sm" showStatus={false} />
        <div className="text-xs text-gray-600">
          {user.currentAction === "editing" && "Editing"}
          {user.currentAction === "dragging" && "Moving"}
          {user.draggedComponent && (
            <span className="text-gray-400 ml-1">component</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};
